import { newId } from "@engram/core/id";
import type { Editor as TEditor } from "@tiptap/core";
import { Eraser, Pen, Redo2, Trash2, Undo2, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { db } from "../../db/client";
import { sha256Hex } from "../attachments";

/*
 * Evernote's Sketch: a drawing saved into the note as a PNG attachment (<img data-attachment data-sketch>).
 * Editing it again opens the PNG on the canvas; saving makes a new attachment and swaps it in (the old
 * file row is dropped with the note's other removed placeholders). Raster on purpose: the note stays plain
 * HTML + an image every app can show.
 */

const W = 1600;
const H = 1000;
const COLORS = ["#211c18", "#d14d41", "#da702c", "#c69b1f", "#4c9a52", "#3a87c8", "#8b6bc9", "#8a8580"];
const SIZES = [3, 6, 12, 24];
const UNDO = 40;

export function SketchDialog({ initial, onSave, onClose }: { initial?: string | null; onSave: (png: Blob) => void; onClose: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [color, setColor] = useState(COLORS[0]!);
  const [size, setSize] = useState(SIZES[1]!);
  const [eraser, setEraser] = useState(false);
  const [undo, setUndo] = useState<ImageData[]>([]);
  const [redo, setRedo] = useState<ImageData[]>([]);
  const [dirty, setDirty] = useState(false);
  const drawing = useRef<{ x: number; y: number; id: number } | null>(null);

  const ctx = () => canvas.current!.getContext("2d", { willReadFrequently: true })!;

  useLayoutEffect(() => {
    const c = ctx();
    c.fillStyle = "#ffffff";
    c.fillRect(0, 0, W, H);
    if (!initial) return;
    const img = new Image();
    img.onload = () => {
      // Keep the old sketch's aspect, fitted into the canvas.
      const k = Math.min(W / img.naturalWidth, H / img.naturalHeight, 1);
      c.drawImage(img, 0, 0, img.naturalWidth * k, img.naturalHeight * k);
    };
    img.src = initial;
  }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const snapshot = () => {
    setUndo((u) => [...u.slice(-(UNDO - 1)), ctx().getImageData(0, 0, W, H)]);
    setRedo([]);
  };
  const restore = (from: ImageData[], setFrom: (v: ImageData[]) => void, to: ImageData[], setTo: (v: ImageData[]) => void) => {
    const last = from.at(-1);
    if (!last) return;
    setTo([...to, ctx().getImageData(0, 0, W, H)]);
    setFrom(from.slice(0, -1));
    ctx().putImageData(last, 0, 0);
    setDirty(true);
  };
  const doUndo = () => restore(undo, setUndo, redo, setRedo);
  const doRedo = () => restore(redo, setRedo, undo, setUndo);

  const point = (e: React.PointerEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  const stroke = (from: { x: number; y: number }, to: { x: number; y: number }, pressure: number) => {
    const c = ctx();
    c.strokeStyle = eraser ? "#ffffff" : color;
    // Pens report pressure; mice and fingers report 0.5 (or 0), which keeps the chosen size.
    c.lineWidth = (eraser ? size * 3 : size) * (pressure > 0 && pressure !== 0.5 ? 0.5 + pressure : 1);
    c.lineCap = "round";
    c.lineJoin = "round";
    c.beginPath();
    c.moveTo(from.x, from.y);
    c.lineTo(to.x, to.y);
    c.stroke();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "z") {
        e.preventDefault();
        if (e.shiftKey) doRedo();
        else doUndo();
      } else if ((e.ctrlKey || e.metaKey) && k === "y") {
        e.preventDefault();
        doRedo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const close = () => {
    if (dirty && !confirm("Discard this sketch?")) return;
    onClose();
  };

  return createPortal(
    <div className="modal-backdrop sketch-backdrop" onKeyDown={(e) => e.key === "Escape" && close()}>
      <div className="sketch" role="dialog" aria-label="Sketch">
        <div className="sketch-bar">
          <button className={`tb-btn${!eraser ? " active" : ""}`} title="Pen" aria-pressed={!eraser} onClick={() => setEraser(false)}>
            <Pen size={17} />
          </button>
          <button className={`tb-btn${eraser ? " active" : ""}`} title="Eraser" aria-pressed={eraser} onClick={() => setEraser(true)}>
            <Eraser size={17} />
          </button>
          <span className="tb-sep" />
          {COLORS.map((c) => (
            <button
              key={c}
              className={`sketch-color${c === color && !eraser ? " on" : ""}`}
              style={{ background: c }}
              title="Colour"
              aria-label={`Colour ${c}`}
              onClick={() => {
                setColor(c);
                setEraser(false);
              }}
            />
          ))}
          <span className="tb-sep" />
          {SIZES.map((s) => (
            <button key={s} className={`sketch-size${s === size ? " on" : ""}`} title={`Size ${s}`} aria-label={`Size ${s}`} onClick={() => setSize(s)}>
              <span style={{ width: Math.min(18, s + 2), height: Math.min(18, s + 2) }} />
            </button>
          ))}
          <span className="tb-sep" />
          <button className="tb-btn" title="Undo (Ctrl+Z)" disabled={!undo.length} onClick={doUndo}>
            <Undo2 size={17} />
          </button>
          <button className="tb-btn" title="Redo (Ctrl+Y)" disabled={!redo.length} onClick={doRedo}>
            <Redo2 size={17} />
          </button>
          <button
            className="tb-btn"
            title="Clear"
            onClick={() => {
              snapshot();
              const c = ctx();
              c.fillStyle = "#ffffff";
              c.fillRect(0, 0, W, H);
              setDirty(true);
            }}
          >
            <Trash2 size={17} />
          </button>
          <span className="sketch-gap" />
          <button className="btn small" onClick={close}>
            Cancel
          </button>
          <button className="btn small primary" onClick={() => canvas.current!.toBlob((b) => b && (onSave(b), onClose()), "image/png")}>
            Save
          </button>
          <button className="icon-btn" title="Close" aria-label="Close" onClick={close}>
            <X size={16} />
          </button>
        </div>
        <div className="sketch-stage">
          <canvas
            ref={canvas}
            width={W}
            height={H}
            className={eraser ? "erasing" : ""}
            onPointerDown={(e) => {
              if (e.button !== 0 && e.pointerType === "mouse") return;
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                /* pointer already gone */
              }
              snapshot();
              const p = point(e);
              drawing.current = { ...p, id: e.pointerId };
              stroke(p, { x: p.x + 0.01, y: p.y }, e.pressure);
              setDirty(true);
            }}
            onPointerMove={(e) => {
              const d = drawing.current;
              if (!d || d.id !== e.pointerId) return;
              // Coalesced events keep fast strokes smooth.
              const co = e.nativeEvent.getCoalescedEvents?.() ?? [];
              const events = co.length ? co : [e.nativeEvent];
              let last = { x: d.x, y: d.y };
              const r = canvas.current!.getBoundingClientRect();
              for (const ev of events) {
                const p = { x: ((ev.clientX - r.left) / r.width) * W, y: ((ev.clientY - r.top) / r.height) * H };
                stroke(last, p, ev.pressure);
                last = p;
              }
              drawing.current = { ...last, id: d.id };
            }}
            onPointerUp={() => (drawing.current = null)}
            onPointerCancel={() => (drawing.current = null)}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Opens the sketch dialog outside React's tree (from the Insert menu or an image's menu). */
export function openSketch(initial: string | null, onSave: (png: Blob) => void) {
  const host = document.createElement("div");
  document.body.append(host);
  const root = createRoot(host);
  const close = () => {
    root.unmount();
    host.remove();
  };
  root.render(<SketchDialog initial={initial} onSave={onSave} onClose={close} />);
}

async function saveSketchFile(noteId: string, png: Blob): Promise<string> {
  const file = new File([png], "Sketch.png", { type: "image/png" });
  const sha = await sha256Hex(file);
  await db.addFile(sha, file);
  const id = newId();
  await db.mutate([{ t: "attachments", id, set: { note_id: noteId, sha256: sha, filename: file.name, mime: "image/png", size: file.size, width: W, height: H, created: Date.now() } }]);
  return id;
}

/** Insert → Sketch: draw, then the PNG lands at the caret. */
export function newSketch(editor: TEditor, noteId: string) {
  const at = editor.state.selection.from;
  openSketch(null, async (png) => {
    const id = await saveSketchFile(noteId, png);
    if (editor.isDestroyed) return;
    editor.chain().focus().insertContentAt(Math.min(at, editor.state.doc.content.size), [{ type: "image", attrs: { attachment: id, alt: "Sketch", sketch: true } }, { type: "paragraph" }]).run();
  });
}

/** Edit sketch: redraw on top of the saved PNG and swap the new file in. */
export function editSketch(editor: TEditor, noteId: string, getPos: () => number | undefined, url: string | null) {
  openSketch(url, async (png) => {
    const id = await saveSketchFile(noteId, png);
    const pos = getPos();
    if (editor.isDestroyed || pos == null) return;
    const node = editor.state.doc.nodeAt(pos);
    if (node?.type.name !== "image") return;
    editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, attachment: id }));
  });
}
